import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { HrEmployee } from '../hr/employee/hr-employee.models';
import { LazyLocationPickerDirective } from '../shared/location-picker/lazy-location-picker.directive';
import { Party } from '../party/party.model';
import { CrmClient } from '../crm/crm.model';
import { FieldWorker } from './field-work.models';

@Component({
  selector: 'app-field-person-create-dialog',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ButtonModule, DatePickerModule, DialogModule, IftaLabelModule,
    InputGroupModule, InputGroupAddonModule, InputTextModule, SelectModule, LazyLocationPickerDirective
  ],
  templateUrl: './field-person-create-dialog.component.html',
  styleUrl: './field-person-create-dialog.component.css'
})
export class FieldPersonCreateDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() saving = false;
  @Input() error = '';
  @Input() employees: HrEmployee[] = [];
  @Input() suppliers: CrmClient[] = [];
  @Input() parties: Party[] = [];
  @Input() newlyCreatedLaborer: Party | null = null;

  // Kept for the legacy label helper; picker options are now server-paged.
  locations: Array<{ id?: number; name?: string; shortDescription?: string }> = [];

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() create = new EventEmitter<FieldWorker>();
  @Output() createLaborer = new EventEmitter<void>();

  form: FieldWorker = this.emptyForm();
  validationError = '';

  readonly sourceOptions = [
    { label: 'Internal', value: 'INTERNAL' },
    { label: 'Freelance', value: 'FREELANCE' },
    { label: 'Supplier worker', value: 'SUPPLIER_WORKER' }
  ];
  readonly statusOptions = ['ACTIVE', 'INACTIVE', 'SUSPENDED', 'UNAVAILABLE'];
  readonly payeeTypeOptions = ['Employee', 'Laborer', 'Company', 'TransportationAgency'];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) this.reset();
    if (changes['newlyCreatedLaborer']?.currentValue?.id?.entityId && this.visible
      && this.form.sourceType !== 'INTERNAL') {
      this.laborerChanged(changes['newlyCreatedLaborer'].currentValue.id.entityId);
    }
  }

  get employeeOptions(): Array<{ label: string; value: number }> {
    return this.employees.filter(row => Boolean(row.employeeId)).map(row => ({
      label: `${row.displayName || [row.firstName, row.lastName].filter(Boolean).join(' ') || 'Employee'}${row.employeeNumber ? ` — ${row.employeeNumber}` : ''}`,
      value: row.employeeId!
    }));
  }

  get supplierOptions(): Array<{ label: string; value: number }> {
    return this.suppliers.filter(row => Boolean(row.id)).map(row => ({
      label: `${row.companyName || row.clientNumber || 'CRM supplier'}${row.clientNumber ? ` — ${row.clientNumber}` : ''}`,
      value: row.id!
    }));
  }

  get payeeOptions(): Array<{ label: string; value: number }> {
    return this.parties
      .filter(row => row.id?.entityType === this.form.payablePartyType)
      .map(row => ({ label: `${row.entityName} — ${row.id.entityType}`, value: row.id.entityId }));
  }

  get laborerOptions(): Array<{ label: string; value: number }> {
    return this.parties.filter(row => row.id?.entityType === 'Laborer').map(row => ({
      label: [row.entityName, row.phoneNumber, row.emailAddress].filter(Boolean).join(' — '),
      value: row.id.entityId
    }));
  }

  get locationOptions(): Array<{ label: string; value: number }> {
    return this.locations.filter(row => Boolean(row.id)).map(row => ({
      label: [row.name, row.shortDescription].filter(Boolean).join(' — '), value: row.id!
    }));
  }

  sourceChanged(): void {
    this.form.hrEmployeeId = undefined;
    this.form.supplierCrmClientId = undefined;
    this.form.payablePartyId = undefined;
    this.form.sourcePartyId = undefined;
    this.form.sourcePartyType = undefined;
    this.form.laborerId = undefined;
    this.form.payablePartyType = this.form.sourceType === 'INTERNAL'
      ? 'Employee' : this.form.sourceType === 'FREELANCE' ? 'Laborer' : 'Company';
  }

  laborerChanged(laborerId?: number): void {
    const laborer = this.parties.find(row => row.id?.entityType === 'Laborer' && row.id.entityId === laborerId);
    if (!laborer || !laborer.id) return;
    this.form.laborerId = laborer.id.entityId;
    this.form.sourcePartyType = 'Laborer';
    this.form.sourcePartyId = laborer.id.entityId;
    this.form.displayName = laborer.entityName;
    this.form.emailAddress = laborer.emailAddress;
    this.form.phoneNumber = laborer.phoneNumber;
    this.form.imageUrl = laborer.imageUrl;
    if (this.form.sourceType === 'FREELANCE') {
      this.form.payablePartyType = 'Laborer';
      this.form.payablePartyId = laborer.id.entityId;
    }
  }

  employeeChanged(employeeId?: number): void {
    const employee = this.employees.find(row => row.employeeId === employeeId);
    if (!employee) return;
    this.form.displayName ||= employee.displayName || [employee.firstName, employee.lastName].filter(Boolean).join(' ');
    this.form.workerCategory ||= employee.workerCategory;
    const party = this.parties.find(row => row.id?.entityType === 'Employee'
      && row.id.entityId === employeeId);
    if (party) {
      this.form.payablePartyType = 'Employee';
      this.form.payablePartyId = party.id.entityId;
      this.form.sourcePartyType = 'Employee';
      this.form.sourcePartyId = party.id.entityId;
    }
  }

  supplierChanged(supplierId?: number): void {
    const supplier = this.suppliers.find(row => row.id === supplierId);
    if (!supplier) return;
    if (supplier.linkedTransportationAgencyId) {
      this.form.payablePartyType = 'TransportationAgency';
      this.form.payablePartyId = supplier.linkedTransportationAgencyId;
    } else if (supplier.linkedCompanyId) {
      this.form.payablePartyType = 'Company';
      this.form.payablePartyId = supplier.linkedCompanyId;
    }
  }

  payeeChanged(partyId?: number): void {
    if (this.form.sourceType === 'FREELANCE') {
      this.form.sourcePartyType = this.form.payablePartyType;
      this.form.sourcePartyId = partyId;
    }
  }

  reset(): void {
    this.form = this.emptyForm();
    this.validationError = '';
  }

  close(): void {
    if (!this.saving) this.visibleChange.emit(false);
  }

  submit(): void {
    this.validationError = '';
    if ((this.form.sourceType === 'FREELANCE' || this.form.sourceType === 'SUPPLIER_WORKER') && !this.form.laborerId) {
      this.validationError = 'Select the individual laborer who will perform the field work.';
      return;
    }
    if (this.form.sourceType === 'SUPPLIER_WORKER' && !this.form.supplierCrmClientId) {
      this.validationError = 'Select the supplying CRM client.';
      return;
    }
    if (!this.form.payablePartyType || !this.form.payablePartyId) {
      this.validationError = 'Select the valid payee for this field person.';
      return;
    }
    this.create.emit({ ...this.form });
  }

  private emptyForm(): FieldWorker {
    return { sourceType: 'INTERNAL', payablePartyType: 'Employee', status: 'ACTIVE' };
  }
}
